/**
 * BOARD HEIGHT — FROZEN IN JS WHILE A TEXT FIELD IS FOCUSED.
 *
 * ── WHY NOT CSS ──────────────────────────────────────────────────────────────────────────────
 * Measured on two real devices (staging viewport probe, 2026-07-31), rest → keyboard open:
 *
 *            sdk.stableHeight   --tg-stable var    --app-h    data-keyboard
 *   Android      848 → 489        847.64 → 489.24   823 → 465    (unset) throughout
 *   iOS          932 → 614        932    → 614      932 → 614    (unset) throughout
 *
 * Two facts kill every CSS-only approach:
 *  1. **Telegram's "stable" height is NOT keyboard-independent** — it drops with the keyboard on
 *     BOTH platforms. So `--tg-viewport-stable-height`, and anything derived from it, shrinks. No
 *     viewport unit escapes either (`100dvh`/`lvh`/`svh`/`vh` all track the layout viewport, which
 *     Android shrinks natively via `interactive-widget=resizes-content`).
 *  2. **`data-keyboard` never fires.** `syncKeyboardInset` decides it from `stable − visible >
 *     120px`, and since `stable` has ALREADY absorbed the keyboard, that difference is ~0 on both
 *     platforms. Any rule scoped to that flag is dead code.
 *
 * ── WHAT THIS DOES INSTEAD ───────────────────────────────────────────────────────────────────
 * It never asks the platform whether a keyboard is open. It remembers the height measured while
 * NOTHING was focused, and pins `--game-h` to that value for as long as a text field owns focus.
 * On a phone a focused text field IS the keyboard — the one signal that flipped reliably on both
 * devices (`focused el: - → input`) while every height signal lied.
 *
 * `--app-h` is deliberately untouched: it SHOULD follow the keyboard, and it is what lifts the
 * bottom nav and the chat composer. That lift has always come from Telegram's shrinking height,
 * not from `data-keyboard`, so nothing here changes it.
 *
 * ── RELEASING ────────────────────────────────────────────────────────────────────────────────
 * Dropping the pin the instant focus leaves would re-expose the keyboard's closing ANIMATION —
 * the board would shrink and grow back, which is the original complaint in miniature. So the pin
 * is held until the live height has either come back to what we remembered (the ordinary case:
 * releasing is then invisible) or held a NEW value long enough to be a real layout change —
 * a rotation or a desktop window resize that happened while typing.
 */

/** Live height differing from the remembered one by more than this is a real change, not rounding. */
const EPSILON_PX = 2;
/**
 * How long a SMALLER height must hold, with nothing focused and the width unchanged, before it is
 * accepted as a new rest rather than a keyboard still on its way out.
 *
 * ⚠️ THIS USED TO BE 250ms AND THAT WAS THE «board re-lays out on close» BUG. A dismissal does not
 * always give the height back in one step: it can PAUSE at an intermediate value on the way up, and
 * a pause longer than the window was accepted as the new rest — so the pin released at the wrong
 * height, the board re-fitted to it, and then re-fitted AGAIN when the height finished returning.
 * Reproduced frame-by-frame (iPhone profile, منچ): table 932→760→932, board 418→392→418, board y
 * 225→139→152→238→225. A keyboard animation is ~250–400ms; a plateau over a second is not one.
 */
export const SETTLE_MS = 1200;

export type FreezeState = {
  /** The height measured at rest — what `--game-h` is pinned to while typing. */
  remembered: number | null;
  /** The px currently pinned onto `--game-h`, or null when CSS owns it. */
  pinned: number | null;
  /** Live value we are waiting to see settle (release path only). */
  candidate: number | null;
  /** When `candidate` was first seen. */
  candidateSince: number | null;
  /** Viewport WIDTH at the moment we froze. A keyboard never changes it; a rotation always does. */
  rememberedWidth: number | null;
};

export const INITIAL_FREEZE_STATE: FreezeState = {
  remembered: null,
  pinned: null,
  candidate: null,
  candidateSince: null,
  rememberedWidth: null,
};

/**
 * The whole decision, pure, so the behaviour can be tested without a device or a keyboard.
 *
 * @param live  the height `--game-h` WOULD have right now if CSS owned it (i.e. `--viewport-h`).
 */
export function nextFreezeState(
  prev: FreezeState,
  input: { typing: boolean; live: number; width: number; now: number },
): FreezeState {
  const { typing, live, width, now } = input;

  if (typing) {
    // Freeze. If we have never measured a rest height (focus before first measure), fall back to
    // the live value — worst case that is exactly today's behaviour, never worse.
    const remembered = prev.remembered ?? live;
    return {
      remembered,
      rememberedWidth: prev.rememberedWidth ?? width,
      pinned: remembered,
      candidate: null,
      candidateSince: null,
    };
  }

  // Nothing focused and nothing pinned: this IS rest — just keep the reference current.
  if (prev.pinned === null) {
    return {
      remembered: live,
      rememberedWidth: width,
      pinned: null,
      candidate: null,
      candidateSince: null,
    };
  }

  const release = (): FreezeState => ({
    remembered: live,
    rememberedWidth: width,
    pinned: null,
    candidate: null,
    candidateSince: null,
  });

  // A ROTATION (or a desktop window resize) changes the WIDTH. A keyboard never does — so a width
  // change is unambiguously a real layout change and is taken immediately.
  if (prev.rememberedWidth !== null && width !== prev.rememberedWidth) return release();

  // The height is back at — or above — what we froze. A keyboard can only ever make the viewport
  // SHORTER, so this is the keyboard gone; releasing here is invisible.
  if (prev.remembered !== null && live >= prev.remembered - EPSILON_PX) return release();

  // Still SHORTER, same width: almost certainly a dismissal in progress, possibly paused partway.
  // Hold the pin. Only a plateau far longer than any keyboard animation is taken as a new rest —
  // the backstop for a genuine shrink (e.g. Telegram restoring its own header) that never returns.
  if (prev.candidate === null || Math.abs(live - prev.candidate) > EPSILON_PX) {
    return { ...prev, candidate: live, candidateSince: now };
  }
  if (prev.candidateSince !== null && now - prev.candidateSince >= SETTLE_MS) return release();
  return prev;
}

/** Whether the board height is currently pinned — i.e. a keyboard is in play, open OR closing. */
export function isBoardHeightPinned(): boolean {
  return currentlyPinned;
}
let currentlyPinned = false;

/** A focused text field is the keyboard. Pure, so the classification is pinned by tests. */
export function isTextEntry(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "TEXTAREA") return true;
  if (tag === "INPUT") {
    // Buttons/checkboxes/etc. are <input> too and open no keyboard.
    const type = (el as HTMLInputElement).type;
    return !NON_TEXT_INPUT_TYPES.has(type);
  }
  return (el as HTMLElement).isContentEditable === true;
}

const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

/**
 * Wire the freeze to the DOM. Idempotent; no-ops without a DOM. Called from `setupViewport`.
 *
 * The live height is READ FROM CSS rather than recomputed here: a hidden probe styled
 * `height: var(--viewport-h)` resolves the very expression `--game-h` uses at rest, so the two can
 * never drift, and `--viewport-h` is never overridden by anyone.
 */
export function startBoardHeightFreeze(): () => void {
  if (typeof document === "undefined") return () => undefined;

  const docEl = document.documentElement;
  const mk = (expr: string): HTMLElement => {
    const d = document.createElement("div");
    d.setAttribute("aria-hidden", "true");
    d.style.cssText = `position:fixed;left:-9999px;top:0;width:1px;pointer-events:none;height:${expr}`;
    document.body.appendChild(d);
    return d;
  };
  const probe = mk("var(--viewport-h)");
  // The inset the tables' bottom strips pad by. Read from the LIVE var, never from the frozen
  // alias, or the freeze would read back its own pinned value.
  const safeProbe = mk("var(--safe-bottom-live)");

  let state = INITIAL_FREEZE_STATE;
  let timer = 0;
  /** The inset measured at rest, and what we last wrote for it. */
  let rememberedSafe: number | null = null;
  let appliedSafe: number | null = null;

  const sync = (): void => {
    const live = probe.getBoundingClientRect().height;
    if (!live) return; // not laid out yet — never freeze a zero
    const typing = isTextEntry(document.activeElement);
    const next = nextFreezeState(state, {
      typing,
      live,
      width: window.innerWidth,
      now: Date.now(),
    });
    if (next.pinned !== state.pinned) {
      if (next.pinned === null) docEl.style.removeProperty("--game-h");
      else docEl.style.setProperty("--game-h", `${next.pinned}px`);
    }
    // Held on exactly the SAME lifecycle as the height — including the CLOSE, where the pin lasts
    // until the height has actually come back — so the two can never disagree by a frame.
    if (next.pinned === null) rememberedSafe = safeProbe.getBoundingClientRect().height;
    const wantSafe = next.pinned === null ? null : rememberedSafe;
    if (wantSafe !== appliedSafe) {
      appliedSafe = wantSafe;
      if (wantSafe === null) docEl.style.removeProperty("--game-safe-bottom");
      else docEl.style.setProperty("--game-safe-bottom", `${wantSafe}px`);
    }
    state = next;
    currentlyPinned = state.pinned !== null;

    // While pinned with nothing focused we are waiting for the height to settle, and the closing
    // keyboard may stop emitting events before it does — so keep checking on our own clock.
    const waiting = state.pinned !== null && !typing;
    if (waiting && !timer) timer = window.setInterval(sync, 80);
    if (!waiting && timer) {
      window.clearInterval(timer);
      timer = 0;
    }
  };

  const vv = window.visualViewport;
  vv?.addEventListener("resize", sync);
  vv?.addEventListener("scroll", sync);
  window.addEventListener("resize", sync);
  window.addEventListener("orientationchange", sync);
  // focusin/focusout BUBBLE (focus/blur do not), so one pair of document-level listeners covers
  // every field in the app, including ones mounted later inside the table-chat sheet.
  document.addEventListener("focusin", sync);
  document.addEventListener("focusout", () => {
    // focusout fires BEFORE the next element gains focus, so moving between two fields would look
    // like "stopped typing" for one frame and unpin. Re-read on the next frame instead.
    requestAnimationFrame(sync);
  });
  sync();

  return () => {
    if (timer) window.clearInterval(timer);
    vv?.removeEventListener("resize", sync);
    vv?.removeEventListener("scroll", sync);
    window.removeEventListener("resize", sync);
    window.removeEventListener("orientationchange", sync);
    document.removeEventListener("focusin", sync);
    probe.remove();
    safeProbe.remove();
    docEl.style.removeProperty("--game-h");
    docEl.style.removeProperty("--game-safe-bottom");
  };
}
